'use client';

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import {
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
  type JSX,
  type KeyboardEvent,
  type ComponentProps,
} from 'react';

import { cn } from '~/utils';
import { Button } from '~/components/button';
import { CaretRightIcon } from '~/components/icons';

export type TCarouselApi = UseEmblaCarouselType[1];
type TUseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type TCarouselOptions = TUseCarouselParameters[0];
type TCarouselPlugin = TUseCarouselParameters[1];

interface ICarouselStateProps {
  opts?: TCarouselOptions;
  plugins?: TCarouselPlugin;
  setApi?: (api: TCarouselApi) => void;
  orientation?: 'horizontal' | 'vertical';
}

interface ICarouselContextProps extends ICarouselStateProps {
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  api: ReturnType<typeof useEmblaCarousel>[1];
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
}

const CarouselContext = createContext<ICarouselContextProps | null>(null);

function useCarousel(): ICarouselContextProps {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

export function Carousel({
  opts,
  setApi,
  plugins,
  children,
  className,
  orientation = 'horizontal',
  ...props
}: ComponentProps<'div'> & ICarouselStateProps): JSX.Element {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback((api: TCarouselApi) => {
    if (!api) {
      return;
    }

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  useEffect(() => {
    if (!api || !setApi) {
      return;
    }

    setApi(api);
  }, [api, setApi]);

  useEffect(() => {
    if (!api) {
      return;
    }

    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        opts,
        api: api,
        scrollPrev,
        scrollNext,
        carouselRef,
        canScrollPrev,
        canScrollNext,
        orientation:
          orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
      }}
    >
      <div
        role='region'
        data-slot='carousel'
        aria-roledescription='carousel'
        onKeyDownCapture={handleKeyDown}
        className={cn('relative', className)}
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

export function CarouselContent({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className='overflow-hidden'
      data-slot='carousel-content'
    >
      <div
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  );
}

export function CarouselItem({
  className,
  ...props
}: ComponentProps<'div'>): JSX.Element {
  const { orientation } = useCarousel();

  return (
    <div
      role='group'
      data-slot='carousel-item'
      aria-roledescription='slide'
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  );
}

export function CarouselPrevious({
  className,
  size = 'icon',
  variant = 'outline',
  ...props
}: ComponentProps<typeof Button>): JSX.Element {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      size={size}
      variant={variant}
      onClick={scrollPrev}
      disabled={!canScrollPrev}
      data-slot='carousel-previous'
      className={cn(
        'absolute size-8 rounded-full border-[#ececec] bg-white hover:bg-white',
        '[--svg-color:var(--color-carbon-500)]',
        orientation === 'horizontal'
          ? 'top-1/2 -left-3.5 -translate-y-1/2'
          : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      {...props}
    >
      <CaretRightIcon className='rotate-180' />
      <span className='sr-only'>Previous slide</span>
    </Button>
  );
}

export function CarouselNext({
  className,
  size = 'icon',
  variant = 'outline',
  ...props
}: ComponentProps<typeof Button>): JSX.Element {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      size={size}
      variant={variant}
      onClick={scrollNext}
      disabled={!canScrollNext}
      data-slot='carousel-next'
      className={cn(
        'absolute size-8 rounded-full border-[#ececec] bg-white hover:bg-white active:bg-white',
        '[--svg-color:var(--color-carbon-500)]',
        orientation === 'horizontal'
          ? 'top-1/2 -right-2.5 -translate-y-1/2'
          : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        className,
      )}
      {...props}
    >
      <CaretRightIcon />
      <span className='sr-only'>Next slide</span>
    </Button>
  );
}
